import { AppIcon } from '@/components/ui/app-icon';
import { useClerk, useUser } from '@clerk/react';
import { useMatch, useNavigate } from '@tanstack/react-router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import * as React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { CopilotIcon } from '@/components/ui/copilot-icon.component';
import { DynamicIcon } from '@/components/ui/icons/dynamic-icon';
import {
  destinationState,
  SIDEBAR_DESTINATIONS,
  SIDEBAR_PAGE_IDS,
  type SidebarPageId,
} from '@/components/sidebar/tabs/sidebar-destinations';
import {
  useCluOpener,
  type CluOpener,
} from '@/components/sidebar/tabs/clu-opener.store';
import { useSidebarNavAccess } from '@/components/sidebar/use-sidebar-nav-access.hook';
import { extensionRouteRedirects } from '@/containers/integrations/extension-tab.util';
import { Feature, type FeatureKey } from '@/constants/features';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useCopilotAccess } from '@/hooks/use-copilot-access.hook';
import { useExtensionNavigation } from '@/hooks/use-extension-navigation.hook';
import {
  useIsCopilotRoute,
  useIsHomeRoute,
} from '@/components/agent-chat/copilot-runtime-provider.component';
import { showIntercom } from '@/hooks/use-intercom.hook';
import { paletteFilter } from './palette-filter';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOpenUpgrade } from '@/hooks/use-open-upgrade.hook';
import { selectTheme, toggleTheme } from '@/store/features/theme.feature';
import {
  setAiChatInitialMessage,
  setAiChatOpen,
} from '@/store/features/ui.feature';
// Not off actions-drawer.data's icon table: that would pull the drawer's whole
// action catalog out of its lazy chunk into first paint.
import { ExportCsvIcon } from '@/components/actions-drawer/action-icons';
import { iconCircleArrowUp, iconCommand, iconFile } from '@/lib/icons';
import {
  useGetDirectoryCompaniesQuery,
  useGetDirectoryPeopleQuery,
} from '@/store/features/directory.feature';
import { useGetLeadListsQuery } from '@/store/features/lead-lists.feature';

const openDocs = () =>
  window.open('https://docs.cleanlist.ai', '_blank', 'noopener,noreferrer');

// Mirrors the sidebar's rest-state icon: regular weight, 16px, muted color.
function NavIcon({ name }: { name: string }) {
  return (
    <DynamicIcon
      name={name}
      weight='regular'
      size={16}
      className='text-muted-foreground'
    />
  );
}

/** Words besides a page's name that find it, such as "contacts" for People. */
const PAGE_KEYWORDS: Partial<Record<SidebarPageId, string[]>> = {
  'find-people': [
    'search',
    'finder',
    'prospects',
    'find leads',
    'new leads',
    'prospect',
    'prospecting',
    'sourcing',
    'icp',
    'discover',
    'build a list',
  ],
  'lead-lists': [
    'lists',
    'leads',
    'my lists',
    'saved leads',
    'contacts',
    'new list',
    'create list',
    'import',
    'csv',
    'upload',
    'enrich',
    'enrichment',
    'smart column',
  ],
  inbox: ['replies', 'messages'],
  sequences: ['campaigns', 'outreach', 'cadences'],
  tasks: ['todo', 'to do'],
  home: ['dashboard', 'clu', 'start', 'overview', 'main'],
  people: [
    'contacts',
    'persons',
    'directory',
    'person',
    'profiles',
    'saved people',
  ],
  companies: [
    'accounts',
    'organizations',
    'directory',
    'orgs',
    'firms',
    'domains',
  ],
  agents: [
    'automations',
    'plays',
    'ai',
    'research',
    'qualification',
    'custom agent',
    'smart agent',
  ],
  reports: ['analytics', 'usage', 'stats', 'metrics', 'performance'],
  integrations: ['crm', 'connections', 'connect', 'sync', 'unified', 'webhook'],
  settings: ['account', 'team', 'preferences'],
};

const EXTENSION_KEYWORDS = ['chrome', 'install', 'linkedin', 'plugin'];

/**
 * Integrations found by name. `id` is the card's `data-integration` on the
 * Integrations page; `flag` hides one the page hides, `workspace` marks a
 * member's own connection, which AppSumo-only orgs don't get (nor flagged
 * ones), `needsAgents` keeps Calendar out until agents unlock, and `feature`
 * marks one the plan doesn't include, which the page shows as a locked Pro
 * card. Names live here rather
 * than off the cards' modules, which would pull the page into first paint.
 */
const INTEGRATION_TARGETS: ReadonlyArray<{
  id: string;
  name: string;
  keywords?: string[];
  flag?: string;
  workspace?: boolean;
  needsAgents?: boolean;
  feature?: FeatureKey;
}> = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    keywords: ['crm'],
    feature: Feature.CRM_INTEGRATIONS,
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    keywords: ['crm', 'sfdc'],
    feature: Feature.CRM_INTEGRATIONS,
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    keywords: ['crm'],
    flag: 'pipedrive_v1',
    feature: Feature.CRM_INTEGRATIONS,
  },
  {
    id: 'attio',
    name: 'Attio',
    keywords: ['crm'],
    flag: 'attio_crm_v1',
    feature: Feature.CRM_INTEGRATIONS,
  },
  { id: 'lemlist', name: 'Lemlist', feature: Feature.CRM_INTEGRATIONS },
  { id: 'slack', name: 'Slack', keywords: ['notifications'] },
  { id: 'google_sheets', name: 'Google Sheets', workspace: true },
  { id: 'google_docs', name: 'Google Docs', workspace: true },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    workspace: true,
    needsAgents: true,
  },
  { id: 'airtable', name: 'Airtable', workspace: true },
  {
    id: 'fireflies',
    name: 'Fireflies',
    keywords: ['meetings'],
    workspace: true,
  },
  { id: 'fathom', name: 'Fathom', keywords: ['meetings'], workspace: true },
  { id: 'gong', name: 'Gong', keywords: ['meetings'], workspace: true },
  { id: 'notion', name: 'Notion', workspace: true },
  { id: 'sixsense', name: '6sense', flag: 'sixsense_v1' },
  { id: 'sixsense_mcp', name: '6sense for Clu', flag: 'sixsense_mcp_v1' },
];

/** The targets the Integrations page shows this org, each with the tier to
 * upgrade to when the plan lacks it. Workspace and flagged cards stay hidden
 * for AppSumo-only orgs, and Calendar needs agents. */
function useIntegrationTargets() {
  const on: Record<string, boolean> = {
    pipedrive_v1: useFeatureFlagEnabled('pipedrive_v1') === true,
    attio_crm_v1: useFeatureFlagEnabled('attio_crm_v1') === true,
    sixsense_v1: useFeatureFlagEnabled('sixsense_v1') === true,
    sixsense_mcp_v1: useFeatureFlagEnabled('sixsense_mcp_v1') === true,
  };
  const { unlocked: agentsUnlocked } = useAgentsAccess();
  const { isAppSumoOnly, isReady, hasFeature, getRequiredTier } =
    useFeatureAccess();
  return INTEGRATION_TARGETS.filter(
    ({ flag, workspace, needsAgents }) =>
      (!flag || on[flag]) &&
      !((flag || workspace) && isAppSumoOnly) &&
      (!needsAgents || agentsUnlocked)
  ).map((target) => ({
    ...target,
    // Not until the plan has loaded, so a paying org never sees a lock flash.
    upgradeTo:
      target.feature && isReady && !hasFeature(target.feature)
        ? getRequiredTier(target.feature)
        : null,
  }));
}

/** "hubspot" opens Integrations scrolled to the HubSpot card, locked with a
 * Pro pill when the plan doesn't include it. */
function IntegrationsGroup({
  runCommand,
}: {
  runCommand: (command: () => void) => void;
}) {
  const navigate = useNavigate();
  const targets = useIntegrationTargets();
  return (
    <CommandGroup heading='Integrations'>
      {targets.map((target) => (
        <CommandItem
          key={target.id}
          value={target.name}
          keywords={target.keywords}
          onSelect={() =>
            runCommand(() =>
              navigate({ to: '/integrations', search: { focus: target.id } })
            )
          }
        >
          <NavIcon name={SIDEBAR_DESTINATIONS.integrations.iconName} />
          <span>{target.name}</span>
          {target.upgradeTo ? (
            <span className='ml-auto flex items-center gap-1.5 text-xs text-muted-foreground'>
              <AppIcon icon={iconCircleArrowUp} aria-hidden />
              Upgrade to {target.upgradeTo}
            </span>
          ) : (
            <RowMeta>Integrations</RowMeta>
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

/** The `G` chord each page answers to (see handleNavChord). */
const PAGE_SHORTCUTS: Partial<Record<SidebarPageId, string>> = {
  home: 'G H',
  'lead-lists': 'G L',
  'find-people': 'G S',
  agents: 'G A',
};

/**
 * What the Navigate group needs from the sidebar's access reads. Called at the
 * palette root, which stays mounted while the dialog content does not, so the
 * extension handshake has settled before the palette opens, as it has for the
 * sidebar, instead of starting over and showing Integrations locked meanwhile.
 */
function useNavigateAccess() {
  const access = useSidebarNavAccess();
  // The installer moved to a tab of Integrations. /extension still works and
  // sends people there when it applies; the palette entry goes there directly.
  const extension = useExtensionNavigation();
  return {
    access,
    extensionInIntegrations: extensionRouteRedirects(extension),
  };
}

/**
 * Every page the sidebar offers this user, in the sidebar's order, read with
 * the sidebar's rules: a page the sidebar hides is left out, and a locked one
 * shows its note and still opens, as the sidebar's entry does.
 */
function NavigateGroup({
  access,
  extensionInIntegrations,
  copilotFullScreen,
  runCommand,
}: ReturnType<typeof useNavigateAccess> & {
  /** Offers the full-screen Copilot after Home, where the Clu rows are not. */
  copilotFullScreen: boolean;
  runCommand: (command: () => void) => void;
}) {
  const navigate = useNavigate();

  return (
    <CommandGroup heading='Navigate'>
      {SIDEBAR_PAGE_IDS.map((id) => {
        const state = destinationState(id, access);
        if (!state.visible) return null;
        const page = SIDEBAR_DESTINATIONS[id];
        const shortcut = PAGE_SHORTCUTS[id];
        return (
          <React.Fragment key={id}>
            <CommandItem
              value={page.label}
              keywords={PAGE_KEYWORDS[id]}
              onSelect={() => runCommand(() => navigate({ to: page.href }))}
            >
              <NavIcon name={page.iconName} />
              <span>{page.label}</span>
              {state.locked && (
                <span className='ml-auto flex items-center gap-1.5 text-xs text-muted-foreground'>
                  <AppIcon icon={iconCircleArrowUp} aria-hidden />
                  {page.lockedTooltip ?? 'Upgrade to unlock'}
                </span>
              )}
              {shortcut && <CommandShortcut>{shortcut}</CommandShortcut>}
            </CommandItem>
            {id === 'home' && copilotFullScreen && (
              <CommandItem
                value='Open Copilot full screen'
                keywords={['clu', 'ai', 'assistant', 'chat', 'copilot']}
                onSelect={() => runCommand(() => navigate({ to: '/clu' }))}
              >
                <NavIcon name='sparkles' />
                <span>Open Copilot full screen</span>
              </CommandItem>
            )}
          </React.Fragment>
        );
      })}
      <CommandItem
        value='Browser extension'
        keywords={EXTENSION_KEYWORDS}
        onSelect={() =>
          runCommand(() =>
            extensionInIntegrations
              ? navigate({ to: '/integrations', search: { tab: 'extension' } })
              : navigate({ to: '/extension' })
          )
        }
      >
        <NavIcon name='browser' />
        <span>Browser extension</span>
      </CommandItem>
    </CommandGroup>
  );
}

/**
 * Clu, first: with text typed, "Ask Clu" starts a new chat with it; with
 * none, "Open Clu". Both open where the tabbed sidebar's chats open, in the
 * Clu dock beside the page. Always shown, whatever the search, so Enter on a
 * query asks Clu.
 */
function CluGroup({
  clu,
  query,
  runCommand,
}: {
  clu: CluOpener;
  query: string;
  runCommand: (command: () => void) => void;
}) {
  const text = query.trim();
  return (
    <CommandGroup forceMount>
      {text ? (
        <CommandItem
          value='Ask Clu'
          forceMount
          onSelect={() => runCommand(() => clu.ask(text))}
        >
          <CopilotIcon />
          <span className='shrink-0'>Ask Clu</span>
          <span className='min-w-0 truncate text-muted-foreground'>{text}</span>
        </CommandItem>
      ) : (
        <CommandItem value='Open Clu' onSelect={() => runCommand(clu.open)}>
          <CopilotIcon />
          <span>Open Clu</span>
          {/* ⌘J opens the dock, so it's named only where Open Clu does too. */}
          {clu.inDock && <CommandShortcut>⌘ J</CommandShortcut>}
        </CommandItem>
      )}
    </CommandGroup>
  );
}

function RowMeta({ children }: { children: React.ReactNode }) {
  return (
    <span className='ml-auto shrink-0 pl-3 text-xs text-muted-foreground'>
      {children}
    </span>
  );
}

const MIN_QUERY_LENGTH = 2;

type ActionGlyph = string | typeof ExportCsvIcon;

function ActionIcon({ icon }: { icon: ActionGlyph }) {
  if (typeof icon === 'string') return <NavIcon name={icon} />;
  const Icon = icon;
  return <Icon className='h-4 w-4 text-muted-foreground' />;
}

const LIST_ACTIONS: ReadonlyArray<{
  label: string;
  icon: ActionGlyph;
  openDrawer?: 'find-email' | 'find-phone' | 'export-csv';
  drawerTab?: 'sources' | 'enrichments' | 'agents';
}> = [
  {
    label: 'Import leads',
    icon: 'user-magnifying-glass',
    drawerTab: 'sources',
  },
  {
    label: 'Enrich leads',
    icon: 'wand-magic-sparkles',
    drawerTab: 'enrichments',
  },
  { label: 'Find email', icon: 'envelope', openDrawer: 'find-email' },
  { label: 'Find phone', icon: 'phone', openDrawer: 'find-phone' },
  // 'agents' is the route's canonical id; the list container maps it onto the
  // drawer's internal 'smart-agents' (CLE-321 rename).
  { label: 'Smart agents', icon: 'robot', drawerTab: 'agents' },
  { label: 'Export CSV', icon: ExportCsvIcon, openDrawer: 'export-csv' },
];

// ponytail: /directory/* has no q= yet, so cmdk filters one page. Adding it
// server-side retires this cap and the overflow row below.
const DIRECTORY_PAGE = { limit: 100, offset: 0 } as const;

/**
 * CommandPalette - global Cmd+K launcher. Clay/Linear/Vercel/Notion all have
 * one; Cleanlist didn't. This is the net-new addition from the second-wave
 * redesign plan.
 *
 * Groups:
 *   1. Clu - ask Clu or open it
 *   2. This list - acts on the lead list you're looking at
 *   3. Lead lists / People / Companies - live results
 *   4. Navigate - every page the sidebar offers this user, plus the extension
 *   5. Preferences - toggle dark mode, keyboard shortcuts help, logout
 *
 * Opens on Cmd+K / Ctrl+K. Closes on Escape or selecting a command.
 */
export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const clerk = useClerk();
  const { user } = useUser();
  const isStaff = Boolean(
    user?.primaryEmailAddress?.emailAddress
      ?.toLowerCase()
      .endsWith('@cleanlist.ai')
  );
  const theme = useAppSelector(selectTheme);
  const isDark = theme === 'dark';
  const navigateAccess = useNavigateAccess();
  // Senders live in sequencing settings: offered only where sequencing is
  // rolled out and on the plan.
  const sendersOffered =
    navigateAccess.access.sequencingEnabled &&
    !navigateAccess.access.sequencingLocked;
  // Clu's rows, from the tabbed sidebar where Clu is available.
  const clu = useCluOpener();
  // The search, emptied as the palette opens, as its own input was on
  // mounting. Kept as it closes, so the dialog animating out still shows
  // what it showed.
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setQuery('');
  }
  const askingClu = clu !== null && query.trim() !== '';
  const { isReady, error, isAppSumoOnly } = useFeatureAccess();
  const hideDirectory = isReady && !error && isAppSumoOnly;
  const { canUseCopilot } = useCopilotAccess();
  const onHomeRoute = useIsHomeRoute();
  const onCopilotRoute = useIsCopilotRoute();

  const listMatch = useMatch({
    from: '/lists/$listId/',
    shouldThrow: false,
  });
  const listId = listMatch?.params.listId;

  // "upgrade" opens the guided wizard.
  const openUpgrade = useOpenUpgrade();
  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length >= MIN_QUERY_LENGTH;
  // The tabbed sidebar's Clu row already asks, first; this one is for the rest.
  const showAskClu = !clu && canUseCopilot && trimmedQuery.length > 0;

  const { data: leadLists } = useGetLeadListsQuery(undefined, {
    skip: !open,
  });
  const { data: people } = useGetDirectoryPeopleQuery(DIRECTORY_PAGE, {
    skip: !searching || hideDirectory,
  });
  const { data: companies } = useGetDirectoryCompaniesQuery(DIRECTORY_PAGE, {
    skip: !searching || hideDirectory,
  });

  const peopleTotal = people?.totalCount ?? null;
  const cappedPeople =
    peopleTotal !== null && peopleTotal > (people?.items.length ?? 0);

  // Buffer for the `G` navigation chord (G L → lists, G S → search, etc.).
  // Cleared after CHORD_TIMEOUT_MS so a stray `G` keystroke doesn't linger.
  const chordTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const chordActiveRef = React.useRef(false);
  const CHORD_TIMEOUT_MS = 1500;

  React.useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return (
        el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.isContentEditable
      );
    };

    const cancelChord = () => {
      chordActiveRef.current = false;
      if (chordTimerRef.current) {
        clearTimeout(chordTimerRef.current);
        chordTimerRef.current = null;
      }
    };

    const startChord = () => {
      chordActiveRef.current = true;
      if (chordTimerRef.current) clearTimeout(chordTimerRef.current);
      chordTimerRef.current = setTimeout(cancelChord, CHORD_TIMEOUT_MS);
    };

    const handleNavChord = (key: string): boolean => {
      switch (key) {
        case 'h':
          navigate({ to: '/' });
          return true;
        case 'l':
          navigate({ to: '/lists' });
          return true;
        case 's':
          navigate({ to: '/finder' });
          return true;
        case 'a':
          navigate({ to: '/agents' });
          return true;
        default:
          return false;
      }
    };

    const down = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K - toggle command palette.
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }

      // Cmd/Ctrl+Shift+L - toggle theme (matches palette hint).
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'l'
      ) {
        e.preventDefault();
        dispatch(toggleTheme());
        return;
      }

      // Skip the rest when typing inside an input/textarea/contenteditable.
      if (isTypingTarget(e.target)) return;

      // `?` - open keyboard shortcuts help.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // Navigation chord: press `G`, then within 1.5s press L / S / A / H.
      if (chordActiveRef.current) {
        const handled = handleNavChord(e.key.toLowerCase());
        cancelChord();
        if (handled) e.preventDefault();
        return;
      }
      if (
        e.key.toLowerCase() === 'g' &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey
      ) {
        startChord();
      }
    };
    const openPalette = () => setOpen(true);
    document.addEventListener('keydown', down);
    window.addEventListener('cleanlist:open-command-palette', openPalette);
    return () => {
      document.removeEventListener('keydown', down);
      window.removeEventListener('cleanlist:open-command-palette', openPalette);
      cancelChord();
    };
  }, [dispatch, navigate]);

  const runCommand = React.useCallback((command: () => void) => {
    setOpen(false);
    // Defer slightly so the dialog close animation can start before navigation.
    setTimeout(command, 50);
  }, []);

  /**
   * Hand the query to Clu on a surface that shows the answer. Home hops to
   * /clu first: its launcher only reveals a conversation through local
   * state, so a seed left in place is answered off screen.
   */
  const askClu = React.useCallback(
    (prompt: string) => {
      const seed = () => dispatch(setAiChatInitialMessage(prompt));
      if (onHomeRoute || onCopilotRoute) {
        // Clearing the flag also wipes any pending seed, so it goes first.
        dispatch(setAiChatOpen(false));
        if (onHomeRoute) {
          void navigate({ to: '/clu' }).then(seed);
          return;
        }
        seed();
        return;
      }
      dispatch(setAiChatOpen(true));
      seed();
    },
    [dispatch, navigate, onCopilotRoute, onHomeRoute]
  );

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen} filter={paletteFilter}>
        <CommandInput
          placeholder='Type a command or search…'
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {/* With Ask Clu offered, every search has a row. */}
          {!askingClu && !showAskClu && (
            <CommandEmpty>No results found.</CommandEmpty>
          )}

          {clu && (
            <>
              <CluGroup clu={clu} query={query} runCommand={runCommand} />
              <CommandSeparator />
            </>
          )}

          {/* The list page's deep-link effect turns these params into an open
              Actions drawer, then strips them (use-leads-table-dialogs.hook). */}
          {listId && (
            <>
              <CommandGroup heading='This list'>
                {LIST_ACTIONS.map((action) => (
                  <CommandItem
                    key={action.label}
                    onSelect={() =>
                      runCommand(() =>
                        navigate({
                          to: '/lists/$listId',
                          params: { listId },
                          // `sheet` is the active tab, not a one-shot trigger:
                          // wiping it would drop the user off Companies.
                          search: (prev) => ({
                            sheet: prev.sheet,
                            openDrawer: action.openDrawer,
                            drawerTab: action.drawerTab,
                          }),
                        })
                      )
                    }
                  >
                    <ActionIcon icon={action.icon} />
                    <span>{action.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandSeparator />
            </>
          )}

          {searching && !!leadLists?.length && (
            <CommandGroup heading='Lead lists'>
              {leadLists.map((list) => (
                <CommandItem
                  key={list.id}
                  // cmdk dedupes by value, and names collide.
                  value={list.id}
                  keywords={[list.name]}
                  onSelect={() =>
                    runCommand(() =>
                      navigate({
                        to: '/lists/$listId',
                        params: { listId: list.id },
                      })
                    )
                  }
                >
                  <NavIcon name='list-bullets' />
                  <span className='truncate'>{list.name}</span>
                  <RowMeta>{list.prospect_count} leads</RowMeta>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {searching && !!people?.items.length && (
            <CommandGroup heading='People'>
              {people.items.map((person) => (
                <CommandItem
                  key={person.id}
                  value={person.id}
                  keywords={[
                    person.name,
                    person.title,
                    person.company_name,
                    person.email,
                  ].filter((k): k is string => !!k)}
                  onSelect={() =>
                    runCommand(() =>
                      navigate({
                        to: '/people/$personId',
                        params: { personId: person.id },
                      })
                    )
                  }
                >
                  <NavIcon name='address-book' />
                  <span className='truncate'>{person.name ?? 'Unnamed'}</span>
                  {(person.title || person.company_name) && (
                    <RowMeta>
                      {[person.title, person.company_name]
                        .filter(Boolean)
                        .join(' · ')}
                    </RowMeta>
                  )}
                </CommandItem>
              ))}
              {cappedPeople && (
                <CommandItem
                  value='people-overflow'
                  forceMount
                  onSelect={() => runCommand(() => navigate({ to: '/people' }))}
                >
                  <NavIcon name='address-book' />
                  <span className='text-muted-foreground'>
                    Showing the {people.items.length} most recent of{' '}
                    {peopleTotal} — open People to search them all
                  </span>
                </CommandItem>
              )}
            </CommandGroup>
          )}

          {searching && !!companies?.items.length && (
            <CommandGroup heading='Companies'>
              {companies.items.map((company) => (
                <CommandItem
                  key={company.id}
                  value={company.id}
                  keywords={[company.name, company.domain].filter(
                    (k): k is string => !!k
                  )}
                  onSelect={() =>
                    runCommand(() =>
                      navigate({
                        to: '/companies/$companyId',
                        params: { companyId: company.id },
                      })
                    )
                  }
                >
                  <NavIcon name='buildings' />
                  <span className='truncate'>
                    {company.name ?? company.domain}
                  </span>
                  <RowMeta>{company.lead_count} leads</RowMeta>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Only while searching, so the idle palette stays short. */}
          {trimmedQuery.length >= MIN_QUERY_LENGTH &&
            destinationState('integrations', navigateAccess.access).visible && (
              <IntegrationsGroup runCommand={runCommand} />
            )}

          <NavigateGroup
            {...navigateAccess}
            copilotFullScreen={!clu && canUseCopilot}
            runCommand={runCommand}
          />

          <CommandSeparator />

          <CommandGroup heading='Preferences'>
            <CommandItem
              keywords={[
                'dark mode',
                'light mode',
                'appearance',
                'theme',
                'night',
              ]}
              onSelect={() => runCommand(() => dispatch(toggleTheme()))}
            >
              <NavIcon name={isDark ? 'sun' : 'moon'} />
              <span>Toggle {isDark ? 'light' : 'dark'} mode</span>
              <CommandShortcut>⌘ ⇧ L</CommandShortcut>
            </CommandItem>
            <CommandItem
              keywords={['api', 'token', 'developer', 'key', 'mcp']}
              onSelect={() => runCommand(() => navigate({ to: '/api-keys' }))}
            >
              <NavIcon name='square-virus' />
              <span>API keys</span>
            </CommandItem>
            <CommandItem
              keywords={['alerts', 'emails', 'preferences', 'unsubscribe']}
              onSelect={() =>
                runCommand(() =>
                  navigate({
                    to: '/settings/notifications',
                    search: { unsubscribe: undefined },
                  })
                )
              }
            >
              <NavIcon name='gear' />
              <span>Notifications</span>
            </CommandItem>
            <CommandItem
              keywords={[
                'profile',
                'password',
                'security',
                'my account',
                'email address',
                '2fa',
                'name',
                'avatar',
              ]}
              onSelect={() => runCommand(() => clerk.openUserProfile())}
            >
              <NavIcon name='user-gear' />
              <span>Account settings</span>
            </CommandItem>
            <CommandItem
              keywords={['workspace', 'company settings', 'org', 'rename org']}
              onSelect={() => runCommand(() => clerk.openOrganizationProfile())}
            >
              <NavIcon name='building' />
              <span>Organization settings</span>
            </CommandItem>
            <CommandItem
              keywords={['members', 'seats', 'invite', 'users', 'permissions']}
              onSelect={() =>
                runCommand(() => navigate({ to: '/settings/team' }))
              }
            >
              <NavIcon name='user-group' />
              <span>Team</span>
            </CommandItem>
            {sendersOffered && (
              <CommandItem
                keywords={[
                  'sender',
                  'email accounts',
                  'mailbox',
                  'smtp',
                  'connect email',
                  'sending account',
                  'gmail',
                  'outlook',
                ]}
                onSelect={() =>
                  runCommand(() =>
                    // /settings/email-accounts is a redirect shim onto this.
                    navigate({
                      to: '/sequencing/settings',
                      search: { section: 'senders' },
                    })
                  )
                }
              >
                <NavIcon name='envelope' />
                <span>Mailboxes</span>
              </CommandItem>
            )}
            <CommandItem
              value='Upgrade plan'
              keywords={['upgrade', 'pro', 'starter', 'compare plans']}
              onSelect={() =>
                runCommand(() => openUpgrade({ source: 'command_palette' }))
              }
            >
              <NavIcon name='circle-arrow-up' />
              <span>Upgrade plan</span>
            </CommandItem>
            <CommandItem
              keywords={[
                'billing',
                'subscription',
                'credits',
                'invoice',
                'payment',
                'pricing',
                'card',
                'plan',
              ]}
              onSelect={() =>
                runCommand(() =>
                  // /plans types every search key as required-but-undefined.
                  navigate({
                    to: '/plans',
                    search: {
                      status: undefined,
                      credits_added: undefined,
                      plan_name: undefined,
                    },
                  })
                )
              }
            >
              <NavIcon name='credit-card' />
              <span>Plans &amp; billing</span>
            </CommandItem>
            {isStaff && (
              <CommandItem
                keywords={['admin', 'staff', 'internal']}
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/management',
                      search: { tab: 'organizations' },
                    })
                  )
                }
              >
                <NavIcon name='users' />
                <span>Management</span>
              </CommandItem>
            )}
            <CommandItem
              keywords={['keys', 'hotkeys', 'shortcuts', 'bindings']}
              onSelect={() => {
                setOpen(false);
                setTimeout(() => setShortcutsOpen(true), 50);
              }}
            >
              <NavIcon name='keyboard' />
              <span>Keyboard shortcuts</span>
              <CommandShortcut>?</CommandShortcut>
            </CommandItem>
            <CommandItem
              keywords={['sign out', 'log out', 'exit', 'leave']}
              onSelect={() =>
                runCommand(() => clerk.signOut({ redirectUrl: '/auth/login' }))
              }
            >
              <NavIcon name='right-from-bracket' />
              <span>Logout</span>
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading='Help'>
            <CommandItem
              keywords={[
                'help',
                'fin',
                'support',
                'contact',
                'chat',
                'talk to a human',
                'report a bug',
                'feedback',
                'faq',
                'how do i',
                'stuck',
                'question',
              ]}
              onSelect={() => runCommand(showIntercom)}
            >
              <NavIcon name='circle-question' />
              <span>Help center</span>
            </CommandItem>
            <CommandItem
              keywords={[
                'docs',
                'documentation',
                'resources',
                'api docs',
                'reference',
              ]}
              onSelect={() => runCommand(openDocs)}
            >
              <NavIcon name='book' />
              <span>Documentation</span>
            </CommandItem>
          </CommandGroup>

          {showAskClu && (
            <>
              <CommandSeparator />
              {/* cmdk hides a group with no matches, force-mounted items
                  included, so the group needs forceMount too. */}
              <CommandGroup forceMount heading='Ask'>
                <CommandItem
                  forceMount
                  value='__ask_clu__'
                  onSelect={() => runCommand(() => askClu(trimmedQuery))}
                >
                  <CopilotIcon className='h-4 w-4' />
                  <span className='truncate'>{trimmedQuery}</span>
                  <span className='ml-auto flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground'>
                    Ask Clu
                    <DynamicIcon
                      name='arrow-right'
                      weight='regular'
                      size={12}
                    />
                  </span>
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>

      <ShortcutsHelpModal
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />
    </>
  );
}

interface ShortcutsHelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ShortcutsHelpModal - `?` key opens this. Lists every global keyboard
 * shortcut so users can discover them without digging through docs.
 */
function ShortcutsHelpModal({ open, onOpenChange }: ShortcutsHelpModalProps) {
  const shortcuts: Array<{
    category: string;
    items: Array<{ keys: string[]; description: string }>;
  }> = [
    {
      category: 'Global',
      items: [
        { keys: ['⌘', 'K'], description: 'Open command palette' },
        { keys: ['⌘', 'J'], description: 'Toggle Clu (new chat from Home)' },
        { keys: ['?'], description: 'Open this shortcuts help' },
        { keys: ['Esc'], description: 'Close modal / drawer / popover' },
      ],
    },
    {
      category: 'Navigation',
      items: [
        { keys: ['G', 'L'], description: 'Go to lead lists' },
        { keys: ['G', 'S'], description: 'Go to people search' },
        { keys: ['G', 'A'], description: 'Go to agents' },
        { keys: ['G', 'H'], description: 'Go to home' },
      ],
    },
    {
      category: 'Lead table',
      items: [{ keys: ['⌘', 'A'], description: 'Open Actions drawer' }],
    },
    {
      category: 'Preferences',
      items: [{ keys: ['⌘', '⇧', 'L'], description: 'Toggle dark mode' }],
    },
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <div className='flex h-[56px] shrink-0 items-center gap-3 border-b border-border/40 px-4'>
        <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground'>
          <AppIcon icon={iconCommand} className='h-4 w-4' />
        </div>
        <div className='flex min-w-0 flex-1 flex-col'>
          <span className='text-[15px] font-semibold text-foreground'>
            Keyboard shortcuts
          </span>
          <span className='text-[12px] text-muted-foreground'>
            Quick reference
          </span>
        </div>
      </div>
      <div className='max-h-[500px] overflow-y-auto p-4'>
        {shortcuts.map((section) => (
          <div key={section.category} className='mb-5 last:mb-0'>
            <h3 className='mb-2 text-[12px] font-medium text-muted-foreground'>
              {section.category}
            </h3>
            <div className='space-y-1.5'>
              {section.items.map((item) => (
                <div
                  key={item.description}
                  className='flex items-center justify-between gap-4 rounded px-2 py-1.5 text-[13px]'
                >
                  <span className='text-foreground'>{item.description}</span>
                  <div className='flex items-center gap-1'>
                    {item.keys.map((key, idx) => (
                      <React.Fragment key={`${key}-${idx}`}>
                        {idx > 0 && (
                          <span className='text-[11px] text-muted-foreground'>
                            then
                          </span>
                        )}
                        <kbd className='inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-border/60 bg-muted/40 px-1.5 text-[11px] font-medium text-muted-foreground'>
                          {key}
                        </kbd>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className='mt-6 flex items-start gap-2 rounded-md border border-border/40 bg-muted/30 p-3'>
          <AppIcon
            icon={iconFile}
            className='h-3.5 w-3.5 shrink-0 text-muted-foreground'
          />
          <p className='text-[12px] text-muted-foreground'>
            Most shortcuts work anywhere in the app. Press{' '}
            <kbd className='inline-flex h-5 items-center justify-center rounded border border-border/60 bg-background px-1 text-[10px]'>
              Esc
            </kbd>{' '}
            to close this help.
          </p>
        </div>
      </div>
    </CommandDialog>
  );
}
